import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { PostCardSpecial } from "./common/PostCardSpecial";
import { PaginatedCardGrid } from "../ng-components/CardGrid";
import { createListFilters } from "../ng-components/Filter/ListFilters";
import { useTheme } from "../ng-components/theme";
import { SeriesBadgeGroup } from "../ng-components/SeriesBadgeGroup";
import { PopularArticlesCard } from "../ng-components/Article/PopularArticlesCard";
import { TextFilter } from "../ng-components/Filter/TextFilter";
import { IndustryFilter } from "../ng-components/Filter/IndustryFilter";
import { createSelectFilter } from "../ng-components/Filter/SelectFilter";
import { NoResults } from "../ng-components/Page/Page";
import queryString from "query-string";
import { getSeriesObj } from "./common/utilities";

export function PostListingPage({ posts, pageConfig, location, products }) {
    const productTags = []
    products.forEach((item) => {
        if (item.wildSectionTagSlug && item.displayName) {
            productTags.push({
                "key": item.wildSectionTagSlug.toLowerCase().replace('#', 'hash-'),
                "label": item.displayName,
            });
        }
    });
    const ListFilters = useMemo(
        () =>
            createListFilters({
                primaryFilters: [
                    {
                        key: "text",
                        component: TextFilter,
                        searchFields: [
                            (item) => item.title,
                            (item) => item.excerpt,
                            (item) => item.tags.map(({ label }) => label),
                            (item) => item.author.name,
                        ],
                    },
                ],
                secondaryFilters: [
                    {
                        key: "product",
                        component: createSelectFilter(
                            "Product",
                            productTags
                        ),
                        searchFields: [(item) => item.products],
                    },
                    {
                        key: "industry",
                        component: IndustryFilter,
                        searchFields: [(item) => item.industries],
                    },
                    {
                        key: "series",
                        component: createSelectFilter(
                            "Series",
                            pageConfig.availableSeries
                        ),
                        searchFields: [(item) => item.series?.slug],
                        initialValue: getSeriesObj(queryString.parse(location.search)?.series)
                    },
                ],
                location
            }),
        [pageConfig]
    );

    return (
        <>
            <ListFilters
                pageConfig={pageConfig}
                items={posts}
                exhibit={
                    <SeriesBadgeGroup seriesList={pageConfig.availableSeries} />
                }
                render={(filteredPosts, isFiltersTouched) => {
                    const cards = (!isFiltersTouched
                        ? [
                              {
                                  key: "popular",
                                  display: (
                                      <PopularArticlesCard
                                          allArticles={filteredPosts}
                                      />
                                  ),
                              },
                          ]
                        : []
                    ).concat(
                        filteredPosts.map(({ id, ...post }) => ({
                            key: id,
                            display: <PostCardSpecial post={post} />,
                        }))
                    );

                    return (
                        <PageContent>
                            {!!filteredPosts.length ? (
                                <PaginatedCardGrid cards={cards} />
                            ) : (
                                <NoResults />
                            )}
                        </PageContent>
                    );
                }}
            />
        </>
    );
}

function PageContent({ children }) {
    const { contentWidth } = useTheme();
    return (
        <div className="page-content">
            {children}
            <style jsx>{`
                .page-content {
                    padding: 0 1.25em;
                    box-sizing: content-box;
                    max-width: ${contentWidth}em;
                    margin: 4.5em auto 0;
                }
            `}</style>
        </div>
    );
}

PageContent.propTypes = {
    children: PropTypes.node.isRequired,
};
